import React, { useState, useEffect } from 'react';
import { COLLECTIONS } from '../../constants/collections';
import Button from '../../components/shared/Button';
import { subscribeToDashboardStats, subscribeToRecentActivities } from '../../services/business/dashboardService';
import { STATUS_COLORS } from '../../constants/statuses';
import DashboardCharts from '../../components/dashboard/DashboardCharts';
import DataTable from '../../components/shared/DataTable';
import { useDataScope } from '../../hooks/useDataScope';

import { getDocks } from '../../services/business/dockService';

const Dashboard = ({ userData }) => {
    const scope = useDataScope(userData);
    const [stats, setStats] = useState({
        pendingReservations: 0,
        vehiclesAtSite: 0,
        delayedReservations: 0,
        efficiency: 0
    });
    const [activities, setActivities] = useState([]);
    const [docks, setDocks] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userData?.tenantId) return;
        setLoading(true);

        // Fetch Docks for mapping IDs to Names if missing
        const fetchDocks = async () => {
            console.log('[Dashboard] Fetching docks for tenant:', userData.tenantId);
            try {
                const dockData = await getDocks(userData.tenantId);
                setDocks(dockData);
            } catch (err) {
                console.error('[Dashboard] Docks fetch error:', err);
            }
        };
        fetchDocks();



        // 1. Subscribe to Stats
        const unsubscribeStats = subscribeToDashboardStats(userData.tenantId, (newStats) => {
            setStats(newStats);
        }, scope);

        // 2. Subscribe to Activities
        const unsubscribeActivities = subscribeToRecentActivities(userData.tenantId, (newActivities) => {
            setActivities(newActivities);
            setLoading(false);
        }, 5, null, scope);

        return () => {
            unsubscribeStats();
            unsubscribeActivities();
        };
    }, [userData?.tenantId]);

    // Helper for Status Colors
    // We need to import STATUS_COLORS or define manually if not imported. 
    // Since imports are top of file, we assume they are available or we use inline map.
    // Let's rely on CSS classes or inline styles map.



    if (loading && !activities.length && !stats) {
        return (
            <div className="flex justify-center items-center" style={{ height: '400px' }}>
                <div className="badge badge-pending">Veriler Yükleniyor...</div>
            </div>
        );
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-xl">
                <div>
                    <h1 style={{ marginBottom: '4px', letterSpacing: '-1px' }}>Merhaba, {userData?.fullName} 👋</h1>
                    <p className="text-secondary" style={{ fontWeight: 'var(--font-bold)' }}>Sistem genel durumu ve bekleyen işlemler aşağıdadır.</p>
                </div>
                <div style={{
                    padding: '8px 16px',
                    background: 'var(--bg-card)',
                    borderRadius: 'var(--radius-lg)',
                    border: '1px solid var(--border-color)',
                    boxShadow: 'var(--shadow-sm)',
                    fontWeight: 'var(--font-semibold)',
                    fontSize: 'var(--text-sm)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--primary-color)' }}>
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                    {new Date().toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 'var(--spacing-lg)', marginBottom: 'var(--spacing-2xl)' }}>
                {/* Stats Cards - Same as before */}
                <div className="card-premium glow-blue" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)', position: 'relative', overflow: 'hidden' }}>
                    <div className="flex justify-between items-start mb-md">
                        <div style={{ width: '48px', height: '48px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(37, 99, 235, 0.15)', color: 'var(--primary-light)', borderRadius: '12px' }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                        </div>
                    </div>
                    <div className="text-secondary mb-xs" style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-bold)', textTransform: 'uppercase', letterSpacing: '1px' }}>Bekleyen Rezervasyonlar</div>
                    <div style={{ fontSize: 'var(--text-3xl)', fontWeight: 'var(--font-bold)', color: 'var(--text-primary)' }}>{stats.pendingReservations}</div>
                </div>

                <div className="card-premium glow-green" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
                    <div className="flex justify-between items-start mb-md">
                        <div style={{ width: '48px', height: '48px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(16, 185, 129, 0.15)', color: 'var(--status-entered)', borderRadius: '12px' }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle></svg>
                        </div>
                    </div>
                    <div className="text-secondary mb-xs" style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-bold)', textTransform: 'uppercase', letterSpacing: '1px' }}>Tesisdeki Araç Sayısı</div>
                    <div style={{ fontSize: 'var(--text-3xl)', fontWeight: 'var(--font-bold)', color: 'var(--text-primary)' }}>{stats.vehiclesAtSite}</div>
                </div>

                <div className="card-premium glow-red" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
                    <div className="flex justify-between items-start mb-md">
                        <div style={{ width: '48px', height: '48px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(239, 68, 68, 0.15)', color: 'var(--status-delayed)', borderRadius: '12px' }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                        </div>
                    </div>
                    <div className="text-secondary mb-xs" style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-bold)', textTransform: 'uppercase', letterSpacing: '1px' }}>Geciken Planlamalar</div>
                    <div style={{ fontSize: 'var(--text-3xl)', fontWeight: 'var(--font-bold)', color: stats.delayedReservations > 0 ? 'var(--status-delayed)' : 'var(--text-primary)' }}>{stats.delayedReservations}</div>
                </div>

                <div className="card-premium glow-purple" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
                    <div className="flex justify-between items-start mb-md">
                        <div style={{ width: '48px', height: '48px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(124, 58, 237, 0.15)', color: 'var(--secondary-color)', borderRadius: '12px' }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>
                        </div>
                    </div>
                    <div className="text-secondary mb-xs" style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-bold)', textTransform: 'uppercase', letterSpacing: '1px' }}>Günlük Rampa Verimliliği</div>
                    <div style={{ fontSize: 'var(--text-3xl)', fontWeight: 'var(--font-bold)', color: 'var(--text-primary)' }}>%{stats.efficiency}</div>
                </div>
            </div>



            <DashboardCharts tenantId={userData?.tenantId} scope={scope} />

            <div className="card-premium glow-cyan" style={{ padding: 'var(--spacing-xl)', background: 'var(--bg-card)', borderColor: 'var(--border-color)', overflow: 'hidden' }}>
                <div style={{ marginBottom: 'var(--spacing-lg)' }}>
                    <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'var(--font-bold)', color: 'var(--text-primary)' }}>
                        Son Aktiviteler ( 5 )
                    </h3>
                </div>
                <DataTable
                    columns={[
                        { header: 'Saat', key: 'time', width: '100px' },
                        {
                            header: 'Firma / Plaka',
                            key: 'plateNumber',
                            render: (row) => (
                                <div className="flex flex-col">
                                    {row.reservationNumber && (
                                        <span className="text-[10px] bg-blue-500/10 text-blue-400 px-1.5 py-0.5 rounded w-fit mb-1 font-mono tracking-wide">
                                            {row.reservationNumber}
                                        </span>
                                    )}
                                    <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{row.plateNumber}</span>
                                    <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{row.transporterName}</span>
                                </div>
                            )
                        },
                        {
                            header: 'Rampa',
                            key: 'dockName',
                            render: (row) => {
                                const dockFromMap = docks.find(d => d.id === row.dockId);
                                const displayName = row.dockName && row.dockName !== 'Rampa Atanmadı'
                                    ? row.dockName
                                    : (dockFromMap?.name || 'Rampa Atanmadı');

                                return (
                                    <div style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>
                                        {displayName}
                                    </div>
                                );
                            }
                        },
                        {
                            header: 'Durum',
                            key: 'status',
                            width: '200px',
                            render: (row) => (
                                <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                                    <div
                                        className="rounded-full text-xs font-bold items-center justify-center shadow-lg transition-transform hover:scale-105"
                                        style={{
                                            display: 'flex',
                                            background: `linear-gradient(135deg, ${STATUS_COLORS[row.status] || '#94a3b8'} 0%, ${STATUS_COLORS[row.status] || '#94a3b8'}cc 100%)`,
                                            color: 'white',
                                            border: '2px solid rgba(255,255,255,0.25)',
                                            borderRadius: '30px',
                                            boxShadow: `0 4px 15px ${STATUS_COLORS[row.status] || '#94a3b8'}50, inset 0 1px 0 rgba(255,255,255,0.2)`,
                                            textShadow: '0 1px 2px rgba(0,0,0,0.4)',
                                            width: '160px',
                                            height: '34px',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {row.statusLabel}
                                    </div>
                                </div>
                            )
                        }
                    ]}
                    data={activities}
                    emptyMessage="Henüz bir aktivite bulunmuyor."
                    pagination={false}
                />
            </div>
        </div >
    );
};

export default Dashboard;
